import { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useMeasures } from '../../hooks/useMeasures';
import { usePipelineNodes } from '../../hooks/usePipelineNodes';
import { measureService } from '../../services/measure.service';
import { ApiError } from '../../services/api.service';
import { toast } from 'sonner';
import { SemanticLayerPageHeader, SemanticLayerToolbar } from '../../components/SemanticLayerPageHeader';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, Settings, Calculator, Hash, Activity, Plus, Search, Lock } from 'lucide-react';
import { useDimensionsList } from '../../hooks/useDimension';
import { formatFormula } from '../../lib/formatFormula';
import type { Measure, MetricType } from '../../services/measure.service';
import type { Dimension } from '../../services/dimension.service';
import type { PipelineNode } from '../../services/pipelineNode.service';
import { useIsInDraftMode } from '../../hooks/useDraftMode';

function getMetricTypeIcon(metricType: MetricType) {
  switch (metricType) {
    case 'CALCULATED':
      return <Calculator className="h-4 w-4 text-muted-foreground" />;
    case 'COHORT':
      return <Activity className="h-4 w-4 text-muted-foreground" />;
    case 'BASIC':
    default:
      return <Hash className="h-4 w-4 text-muted-foreground" />;
  }
}

function getMetricTypeBadge(metricType: MetricType) {
  switch (metricType) {
    case 'CALCULATED':
      return (
        <Badge variant="outline" className="border-purple-500 text-purple-700 dark:text-purple-400 dark:border-purple-400">
          Calculated
        </Badge>
      );
    case 'COHORT':
      return (
        <Badge variant="outline" className="border-blue-500 text-blue-700 dark:text-blue-400 dark:border-blue-400">
          Cohort
        </Badge>
      );
    case 'BASIC':
    default:
      return (
        <Badge variant="secondary">
          Basic
        </Badge>
      );
  }
}

export default function Metrics() {
  const isInDraftMode = useIsInDraftMode();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
  const pageSize = 50;

  // New metric dialog state
  const [newMetricDialogOpen, setNewMetricDialogOpen] = useState(false);
  const [newMetricDialogName, setNewMetricDialogName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery]);

  const { data, isLoading, error } = useMeasures({
    page,
    pageSize,
    search: debouncedSearchQuery,
    sort: 'name',
  });

  // Fetch pipeline nodes to display entity names
  const { data: nodesData, isLoading: nodesLoading } = usePipelineNodes({
    page: 1,
    pageSize: 1000,
  });

  // Fetch dimensions for COHORT formula display
  const { data: dimensionsData, isLoading: dimensionsLoading } = useDimensionsList({
    pageSize: 1000,
  });

  const measures: Measure[] = data?.records || [];
  const totalPages = data?.total_pages || 1;

  // Build lookup maps
  const nodesById: Record<string, PipelineNode> = {};
  if (nodesData?.records) {
    for (const node of nodesData.records) {
      nodesById[node.id] = node;
    }
  }

  const measuresById: Record<string, Measure> = {};
  if (data?.records) {
    for (const m of data.records) {
      measuresById[m.id] = m;
    }
  }

  const dimensionsById: Record<string, Dimension> = {};
  if (dimensionsData?.records) {
    for (const d of dimensionsData.records) {
      dimensionsById[d.id] = d;
    }
  }

  const handleConfigure = (measureId: string) => {
    navigate(`/develop/semantic-layer/metrics/${measureId}`);
  };

  const handleCreateMetric = async () => {
    if (!newMetricDialogName.trim()) return;

    setIsCreating(true);
    try {
      const trimmedName = newMetricDialogName.trim();
      const newMeasure = await measureService.create({
        name: trimmedName,
        column_name: toSqlSafeColumnName(trimmedName),
        metric_type: 'BASIC',
      });

      queryClient.invalidateQueries({ queryKey: ['measures'] });
      setNewMetricDialogOpen(false);
      setNewMetricDialogName('');
      navigate(`/develop/semantic-layer/metrics/${newMeasure.id}`);
    } catch (error) {
      console.error('Failed to create metric:', error);
      const message =
        error instanceof ApiError
          ? error.payload?.msg || error.message
          : 'Failed to create metric';
      toast.error(message);
    } finally {
      setIsCreating(false);
    }
  };

  // Convert a name to a SQL-safe column name (uppercase with underscores)
  const toSqlSafeColumnName = (name: string): string => {
    return name
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_') // Replace non-alphanumeric chars with underscores
      .replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
  };

  const isLoadingAll = isLoading || nodesLoading || dimensionsLoading;

  return (
    <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh)' }}>
      <SemanticLayerPageHeader tab="metrics" />
      <SemanticLayerToolbar>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search metrics..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-64 pl-9"
          />
        </div>
        {isInDraftMode && (
          <Button
            onClick={() => setNewMetricDialogOpen(true)}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            New Metric
          </Button>
        )}
      </SemanticLayerToolbar>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-8 min-h-0">
        {isLoadingAll ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading metrics...</p>
            </div>
          </div>
        ) : error ? (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-destructive text-sm">
              Failed to load metrics. Please try again.
            </p>
          </div>
        ) : measures.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-muted-foreground">No metrics found.</p>
          </div>
        ) : (
          <>
            <div className="rounded-md border">
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40%]">Name</TableHead>
                    <TableHead className="w-[15%]">Type</TableHead>
                    <TableHead className="w-[25%]">Formula</TableHead>
                    <TableHead className="w-[20%] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {measures.map((measure: Measure) => (
                    <TableRow key={measure.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-start gap-2">
                          <div className="flex-shrink-0 w-4 h-4 mt-0.5">
                            {getMetricTypeIcon(measure.metric_type)}
                          </div>
                          {measure.managed_by_template_id && (
                            <Lock className="h-3 w-3 text-muted-foreground/60 shrink-0" />
                          )}
                          <div className="flex flex-col min-w-0">
                            <span>{measure.name}</span>
                            {measure.description && (
                              <span className="text-xs text-muted-foreground font-normal">
                                {measure.description}
                              </span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getMetricTypeBadge(measure.metric_type)}
                      </TableCell>
                      <TableCell className="text-muted-foreground font-mono text-sm">
                        {formatFormula(measure, nodesById, measuresById, dimensionsById)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleConfigure(measure.id)}
                          title="Configure"
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (page > 1) setPage(page - 1);
                        }}
                        style={{ pointerEvents: page === 1 ? 'none' : 'auto', opacity: page === 1 ? 0.5 : 1 }}
                      />
                    </PaginationItem>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= page - 1 && pageNum <= page + 1)
                      ) {
                        return (
                          <PaginationItem key={pageNum}>
                            <PaginationLink
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setPage(pageNum);
                              }}
                              isActive={pageNum === page}
                            >
                              {pageNum}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      } else if (pageNum === page - 2 || pageNum === page + 2) {
                        return (
                          <PaginationItem key={`ellipsis-${pageNum}`}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        );
                      }
                      return null;
                    })}

                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (page < totalPages) setPage(page + 1);
                        }}
                        style={{ pointerEvents: page === totalPages ? 'none' : 'auto', opacity: page === totalPages ? 0.5 : 1 }}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        )}
      </div>

      {/* New Metric Dialog */}
      <Dialog open={newMetricDialogOpen} onOpenChange={setNewMetricDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Metric</DialogTitle>
            <DialogDescription>
              Create a new metric. You can configure its details after creation.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="new-metric-dialog-name">Metric Name</Label>
              <Input
                id="new-metric-dialog-name"
                value={newMetricDialogName}
                onChange={(e) => setNewMetricDialogName(e.target.value)}
                placeholder="Enter metric name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newMetricDialogName.trim()) {
                    handleCreateMetric();
                  }
                }}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewMetricDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateMetric}
              disabled={!newMetricDialogName.trim() || isCreating}
            >
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Metric'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
